"use client";
import React from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import { cn } from "@/lib/utils";

export const MenuItem = React.forwardRef<
	HTMLDivElement,
	{
		setActive: (item: string | null) => void;
		active: string | null;
		item: string;
		children: React.ReactNode;
		fullWidth?: boolean;
		dropdownClassName?: string;
	}
>((props, ref) => {
	const { setActive, active, item, children, fullWidth } = props;
	return (
		<div ref={ref} onMouseEnter={() => setActive(item)} className="relative flex h-full items-center">
			<motion.p
				animate={{
					color: active === item ? "var(--primary)" : "currentColor",
				}}
				transition={{ duration: 0.2 }}
				className={cn(
					"cursor-pointer font-syne text-sm font-bold uppercase tracking-wide px-4 py-2 rounded-sm transition-colors hover:bg-accent hover:text-accent-foreground",
					active === item && "bg-accent text-accent-foreground"
				)}
			>
				{item}
			</motion.p>
			{active === item && (
				<motion.div
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3, ease: "easeOut" }}
					className={cn(
						"z-50 pt-6",
						fullWidth
							? "fixed top-20 left-0 right-0 w-full pt-0"
							: "absolute top-full left-1/2 -translate-x-1/2 transform",
					)}
				>
					{/* Invisible bridge to prevent menu closing */}
					<div className="absolute -top-6 left-0 right-0 h-6 bg-transparent" />

					<div
						className={cn(
							"overflow-hidden bg-white shadow-xl dark:bg-zinc-950",
							fullWidth
								? "w-full border-b border-zinc-200 dark:border-zinc-800"
								: "rounded-xl border border-zinc-200 dark:border-zinc-800",
							props.dropdownClassName
						)}
					>
						<div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 mix-blend-overlay"></div>
						<div className="relative z-10">{children}</div>
					</div>
				</motion.div>
			)
			}
		</div >
	);
});
MenuItem.displayName = "MenuItem";

export const Menu = React.forwardRef<
	HTMLDivElement,
	{
		setActive: (item: string | null) => void;
		children: React.ReactNode;
	}
>((props, ref) => {
	const { setActive, children } = props;
	return (
		<nav
			ref={ref}
			onMouseLeave={() => setActive(null)}
			className="flex h-full items-center space-x-8"
		>
			{children}
		</nav>
	);
});
Menu.displayName = "Menu";

export const HoveredLink = React.forwardRef<
	HTMLAnchorElement,
	{
		href: string;
		children: React.ReactNode;
		className?: string;
	}
>((props, ref) => {
	const { href, children, className } = props;
	return (
		<Link
			ref={ref}
			href={href}
			className={cn(
				"text-neutral-700 hover:text-primary dark:text-neutral-200 dark:hover:text-primary",
				className,
			)}
		>
			{children}
		</Link>
	);
});
HoveredLink.displayName = "HoveredLink";

export const ProductItem = React.forwardRef<
	HTMLAnchorElement,
	{
		title: string;
		description: string;
		href: string;
		src: string;
		className?: string;
	}
>((props, ref) => {
	const { title, description, href, src, className } = props;
	return (
		<Link
			ref={ref}
			href={href}
			prefetch={true}
			className={cn(
				"group relative flex flex-col overflow-hidden rounded-xl bg-zinc-50 dark:bg-zinc-900",
				className,
			)}
		>
			<div className="relative h-[240px] w-full overflow-hidden">
				<Image
					src={src}
					alt={title}
					fill
					className="object-cover transition-transform duration-700 group-hover:scale-110"
				/>
				<div className="absolute inset-0 bg-black/20 transition-opacity duration-300 group-hover:opacity-0" />

				{/* Overlay on hover */}
				<div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 backdrop-blur-[2px] transition-all duration-300 group-hover:opacity-100">
					<span className="rounded-full bg-white/20 px-4 py-2 font-syne text-xs font-bold uppercase text-white backdrop-blur-md transition-transform duration-300 group-hover:scale-105">
						View Brand
					</span>
				</div>
			</div>

			<div className="relative z-10 flex flex-1 flex-col justify-between p-4 transition-colors duration-300 group-hover:bg-zinc-100 dark:group-hover:bg-zinc-800">
				<div>
					<h3 className="font-syne text-base font-bold uppercase text-foreground group-hover:text-primary transition-colors">
						{title}
					</h3>
					<p className="mt-2 line-clamp-2 font-inter text-xs text-muted-foreground dark:text-zinc-300">
						{description}
					</p>
				</div>
				<div className="mt-4 flex items-center gap-2 text-xs font-bold uppercase text-primary opacity-0 transition-all duration-300 -translate-x-2 group-hover:translate-x-0 group-hover:opacity-100">
					<span>Explore</span>
					<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
						<path d="M7 17L17 7" />
						<path d="M7 7h10v10" />
					</svg>
				</div>
			</div>
		</Link>
	);
});
ProductItem.displayName = "ProductItem";
